// SPDX-License-Identifier: LicenseRef-Proprietary

import { useState, useCallback } from 'react';

export interface AnalysisProgress {
  total: number;
  completed: number;
  failed: number;
  currentPaper?: string;
  estimatedTimeRemaining?: number;
  startTime: Date;
  individualStatus: Record<string, 'pending' | 'analyzing' | 'completed' | 'failed'>;
  // NEW: Dynamic time estimation fields
  averageTimePerPaper?: number;
  lastUpdateTime: Date;
  timeEstimationHistory: number[]; // Track completion times for better estimation
}

export interface AnalysisProgressHook {
  progress: AnalysisProgress | null;
  startProgress: (totalPapers: number, paperIds: string[]) => void;
  updatePaperStatus: (pmid: string, status: 'analyzing' | 'completed' | 'failed') => void;
  completeProgress: () => void;
  resetProgress: () => void;
  calculateTimeRemaining: (progress: AnalysisProgress) => number | undefined;
  // NEW: Dynamic time estimation methods
  updateTimeEstimation: () => void;
  getAverageTimePerPaper: () => number | undefined;
}

export const useAnalysisProgress = (): AnalysisProgressHook => {
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);

  const calculateTimeRemaining = useCallback((currentProgress: AnalysisProgress): number | undefined => {
    if (currentProgress.completed === 0) return undefined;
    
    const elapsed = Date.now() - currentProgress.startTime.getTime();
    
    // Use average time per paper if available for better estimation
    let rate: number;
    if (currentProgress.averageTimePerPaper && currentProgress.averageTimePerPaper > 0) {
      rate = 1 / currentProgress.averageTimePerPaper; // papers per millisecond
    } else {
      rate = currentProgress.completed / elapsed; // fallback to simple rate
    }
    
    const remaining = currentProgress.total - currentProgress.completed - currentProgress.failed;
    
    if (remaining <= 0 || rate <= 0) return undefined;
    
    return remaining / rate; // milliseconds remaining
  }, []);

  const startProgress = useCallback((totalPapers: number, paperIds: string[]) => {
    const initialStatus: Record<string, 'pending' | 'analyzing' | 'completed' | 'failed'> = {};
    paperIds.forEach(pmid => {
      initialStatus[pmid] = 'pending';
    });

    const now = new Date();
    setProgress({
      total: totalPapers,
      completed: 0,
      failed: 0,
      startTime: now,
      lastUpdateTime: now,
      individualStatus: initialStatus,
      timeEstimationHistory: [],
    });
  }, []);

  const updatePaperStatus = useCallback((pmid: string, status: 'analyzing' | 'completed' | 'failed') => {
    setProgress(prev => {
      if (!prev) return null;

      const now = new Date();
      const newIndividualStatus = { ...prev.individualStatus };
      const oldStatus = newIndividualStatus[pmid];
      newIndividualStatus[pmid] = status;

      let newCompleted = prev.completed;
      let newFailed = prev.failed;
      let currentPaper = prev.currentPaper;
      let newTimeEstimationHistory = [...prev.timeEstimationHistory];
      let newAverageTimePerPaper = prev.averageTimePerPaper;

      // Update counters based on status change
      if (status === 'completed' && oldStatus !== 'completed') {
        newCompleted++;
        currentPaper = undefined; // Clear current paper when completed
        
        // Calculate time for this paper and update estimation history
        const paperStartTime = prev.lastUpdateTime.getTime();
        const paperCompletionTime = now.getTime() - paperStartTime;
        newTimeEstimationHistory.push(paperCompletionTime);
        
        // Keep only last 10 completion times for rolling average
        if (newTimeEstimationHistory.length > 10) {
          newTimeEstimationHistory = newTimeEstimationHistory.slice(-10);
        }
        
        // Calculate new average time per paper
        newAverageTimePerPaper = newTimeEstimationHistory.reduce((sum, time) => sum + time, 0) / newTimeEstimationHistory.length;
      } else if (status === 'failed' && oldStatus !== 'failed') {
        newFailed++;
        currentPaper = undefined; // Clear current paper when failed
      } else if (status === 'analyzing') {
        currentPaper = pmid;
      }

      const updatedProgress = {
        ...prev,
        completed: newCompleted,
        failed: newFailed,
        currentPaper,
        lastUpdateTime: now,
        individualStatus: newIndividualStatus,
        timeEstimationHistory: newTimeEstimationHistory,
        averageTimePerPaper: newAverageTimePerPaper,
      };

      // Calculate estimated time remaining
      updatedProgress.estimatedTimeRemaining = calculateTimeRemaining(updatedProgress);

      return updatedProgress;
    });
  }, [calculateTimeRemaining]);

  const completeProgress = useCallback(() => {
    setProgress(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        currentPaper: undefined,
        estimatedTimeRemaining: undefined,
      };
    });
  }, []);

  const resetProgress = useCallback(() => {
    setProgress(null);
  }, []);

  const updateTimeEstimation = useCallback(() => {
    setProgress(prev => {
      if (!prev) return null;
      
      const updatedProgress = {
        ...prev,
        lastUpdateTime: new Date(),
      };
      
      // Recalculate estimated time remaining
      updatedProgress.estimatedTimeRemaining = calculateTimeRemaining(updatedProgress);
      
      return updatedProgress;
    });
  }, [calculateTimeRemaining]);

  const getAverageTimePerPaper = useCallback((): number | undefined => {
    const currentProgress = progress;
    return currentProgress?.averageTimePerPaper;
  }, [progress]);

  return {
    progress,
    startProgress,
    updatePaperStatus,
    completeProgress,
    resetProgress,
    calculateTimeRemaining,
    updateTimeEstimation,
    getAverageTimePerPaper,
  };
};